import { FlexCol, FlexRow } from '@/components/common/Flex';
import Icon from '@/components/common/icons/Icon';
import * as alertIcons from '@/components/common/icons/untitled/AlertIcons';
import * as arrowIcons from '@/components/common/icons/untitled/ArrowIcons';
import * as chartsIcons from '@/components/common/icons/untitled/ChartsIcons';
import * as communicationIcons from '@/components/common/icons/untitled/CommunicationIcons';
import * as developmentIcons from '@/components/common/icons/untitled/DevelopmentIcons';
import * as editorIcons from '@/components/common/icons/untitled/EditorIcons';
import * as filesIcons from '@/components/common/icons/untitled/FilesIcons';
import * as financesIcons from '@/components/common/icons/untitled/FinanceIcons';
import * as generalIcons from '@/components/common/icons/untitled/GeneralIcons';
import { SearchSM } from '@/components/common/icons/untitled/GeneralIcons';
import * as imagesIcons from '@/components/common/icons/untitled/ImagesIcons';
import * as layoutIcons from '@/components/common/icons/untitled/LayoutIcons';
import * as mapsIcons from '@/components/common/icons/untitled/MapsIcons';
import * as mediaDevicesIcons from '@/components/common/icons/untitled/MediaDevicesIcons';
import * as securityIcons from '@/components/common/icons/untitled/SecurityIcons';
import * as shapesIcons from '@/components/common/icons/untitled/ShapesIcons';
import * as timeIcons from '@/components/common/icons/untitled/TimeIcons';
import * as usersIcons from '@/components/common/icons/untitled/UsersIcons';
import * as weatherIcons from '@/components/common/icons/untitled/WeatherIcons';
import InputField from '@/components/common/InputField';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import {
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { CUSTOM_ICONS } from '@common/react/cashflow';
import { IconName } from '@common/react/icons';
import { debounce } from '@common/util/debounce';
import React, { useMemo, useState } from 'react';

interface IconSelectProps {
    onChange: (iconName: IconName) => void;
    value: IconName;
}

const iconGroups = [
    {
        groupName: 'Alerts',
        iconNames: Object.keys(alertIcons),
    },
    {
        groupName: 'Arrows',
        iconNames: Object.keys(arrowIcons),
    },
    {
        groupName: 'Charts',
        iconNames: Object.keys(chartsIcons),
    },
    {
        groupName: 'Communication',
        iconNames: Object.keys(communicationIcons),
    },
    {
        groupName: 'Custom',
        iconNames: CUSTOM_ICONS,
    },
    {
        groupName: 'Development',
        iconNames: Object.keys(developmentIcons),
    },
    {
        groupName: 'Editor',
        iconNames: Object.keys(editorIcons),
    },
    {
        groupName: 'Files',
        iconNames: Object.keys(filesIcons),
    },
    {
        groupName: 'Finances',
        iconNames: Object.keys(financesIcons),
    },
    {
        groupName: 'General',
        iconNames: Object.keys(generalIcons),
    },
    {
        groupName: 'Images',
        iconNames: Object.keys(imagesIcons),
    },
    {
        groupName: 'Layout',
        iconNames: Object.keys(layoutIcons),
    },
    {
        groupName: 'Maps',
        iconNames: Object.keys(mapsIcons),
    },
    {
        groupName: 'Media Devices',
        iconNames: Object.keys(mediaDevicesIcons),
    },
    {
        groupName: 'Security',
        iconNames: Object.keys(securityIcons),
    },
    {
        groupName: 'Shapes',
        iconNames: Object.keys(shapesIcons),
    },
    {
        groupName: 'Time',
        iconNames: Object.keys(timeIcons),
    },
    {
        groupName: 'Users',
        iconNames: Object.keys(usersIcons),
    },
    {
        groupName: 'Weather',
        iconNames: Object.keys(weatherIcons),
    },
];

const IconSelect = ({ onChange, value }: IconSelectProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const debouncedSetSearchQuery = useMemo(() => {
        // Rerendering the icon set is expensive - debounce to avoid lag
        return debounce(setSearchQuery, 300);
    }, [setSearchQuery]);

    return (
        <Popover
            trigger="click"
            isLazy
            isOpen={isOpen}
            placement="bottom-start"
            onOpen={() => {
                setIsOpen(true);
            }}
            onClose={() => {
                setIsOpen(false);
            }}
        >
            <PopoverTrigger>
                <FlexRow
                    h={12}
                    w={12}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    align="center"
                    justify="center"
                    tabIndex={0}
                    {...hoverStyleProps({ bg: 'gray.50' })}
                >
                    <Icon name={value} />
                </FlexRow>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverBody>
                    <InputField
                        my={4}
                        name="search"
                        type="text"
                        inputProps={{
                            variant: 'outline',
                            borderRadius: 'full',
                            size: 'sm',
                        }}
                        placeholder="Search icons..."
                        value={searchQuery}
                        onChange={debouncedSetSearchQuery}
                        iconElement={<SearchSM size="sm" />}
                    />
                    <FlexCol maxH={400} overflowY="scroll" gap={5}>
                        {iconGroups.map(({ groupName, iconNames }) => (
                            <IconGroup
                                groupName={groupName}
                                iconNames={iconNames as IconName[]}
                                query={searchQuery}
                                onSelectIcon={(iconName) => {
                                    onChange(iconName);
                                    setIsOpen(false);
                                }}
                                key={groupName}
                            />
                        ))}
                    </FlexCol>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

const IconGroup = ({
    groupName,
    iconNames,
    query,
    onSelectIcon,
}: {
    groupName: string;
    iconNames: IconName[];
    query: string;
    onSelectIcon: (iconName: IconName) => void;
}) => {
    const matchingIconNames = iconNames.filter(
        (iconName) =>
            query === '' || iconName.toLowerCase().includes(query.toLowerCase())
    );

    if (matchingIconNames.length === 0) {
        return null;
    }

    return (
        <FlexCol gap={2}>
            <Text
                color="textMuted"
                fontSize="md"
                fontWeight="bold"
                mb={1}
                ml={2}
            >
                {groupName}
            </Text>
            <FlexRow flexWrap="wrap">
                {matchingIconNames.map((iconName) => (
                    <Tooltip
                        label={iconName}
                        aria-label={iconName}
                        key={iconName}
                    >
                        <FlexRow
                            p={1}
                            m={1}
                            borderRadius="md"
                            onClick={() => {
                                onSelectIcon(iconName);
                            }}
                            {...hoverStyleProps({ bg: 'gray.100' })}
                        >
                            <Icon name={iconName} />
                        </FlexRow>
                    </Tooltip>
                ))}
            </FlexRow>
        </FlexCol>
    );
};

export default IconSelect;
